import React from 'react';
import { I } from '../../common/v5/config';

/**
 * Error Boundary for Agent Components
 * Catches JavaScript errors anywhere in the agent component tree
 */
class AgentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and error tracking service
    console.error('Agent component error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });


    if (window.errorTracker && typeof window.errorTracker.captureException === 'function') {
      window.errorTracker.captureException(error, {
        tags: {
          component: 'AgentErrorBoundary',
          section: 'agent-management'
        },
        extra: {
          errorInfo: errorInfo,
          componentStack: errorInfo.componentStack
        }
      });
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          context={this.props.context || 'agent'}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Error Fallback Component
 * Displays when an error occurs in agent components
 */
const ErrorFallback = ({ error, errorInfo, onRetry, context }) => {
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <div className="agent-error-boundary">
      <div className="error-container">
        <div className="error-icon">
          <i className="icon-exclamation-triangle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
        </div>
        
        <div className="error-content">
          <h3 className="error-title">
            {I('Something went wrong')}
          </h3>
          
          <p className="error-message">
            {context === 'agent-form' 
              ? I('There was an error with the agent form. Please try again or contact support if the problem persists.')
              : context === 'agent-list'
              ? I('There was an error loading the agent list. Please refresh the page or contact support.')
              : I('An unexpected error occurred in the agent management system.')
            }
          </p>

          <div className="error-actions">
            <button 
              className="btn btn-primary" 
              onClick={onRetry}
              style={{ marginRight: '10px' }}
            >
              <i className="icon-refresh"></i> {I('Try Again')}
            </button>
            
            <button 
              className="btn btn-secondary" 
              onClick={() => window.location.reload()}
            >
              <i className="icon-reload"></i> {I('Reload Page')}
            </button>
          </div>

          {isDevelopment && error && (
            <details className="error-details" style={{ marginTop: '20px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                {I('Error Details (Development Only)')}
              </summary>
              <div className="error-stack" style={{ 
                marginTop: '10px', 
                padding: '10px', 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                maxHeight: '300px'
              }}>
                <strong>Error:</strong> {error.toString()}
                {error.stack && (
                  <>
                    <br /><br />
                    <strong>Stack Trace:</strong>
                    <br />
                    {error.stack}
                  </>
                )}
                {errorInfo && errorInfo.componentStack && (
                  <>
                    <br /><br />
                    <strong>Component Stack:</strong>
                    <br />
                    {errorInfo.componentStack}
                  </>
                )}
              </div>
            </details>
          )}
        </div>
      </div>

      <style jsx>{`
        .agent-error-boundary {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
          padding: 20px;
        }

        .error-container {
          text-align: center;
          max-width: 600px;
          padding: 40px;
          background: #ffffff;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .error-icon {
          margin-bottom: 20px;
        }

        .error-title {
          color: #dc3545;
          margin-bottom: 15px;
          font-size: 24px;
          font-weight: 600;
        }

        .error-message {
          color: #6c757d;
          margin-bottom: 25px;
          font-size: 16px;
          line-height: 1.5;
        }

        .error-actions {
          margin-bottom: 20px;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #545b62;
        }

        .error-details {
          text-align: left;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 10px;
          background-color: #f8f9fa;
        }

        .error-details summary {
          padding: 5px;
          background-color: #e9ecef;
          border-radius: 4px;
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  );
};

/**
 * Higher-order component to wrap components with error boundary
 */
export const withAgentErrorBoundary = (Component, context = 'agent') => {
  return function WrappedComponent(props) {
    return (
      <AgentErrorBoundary context={context}>
        <Component {...props} />
      </AgentErrorBoundary>
    );
  };
};

/**
 * Hook to handle async errors in components
 */
export const useAsyncError = () => {
  const [_, setError] = React.useState();
  return React.useCallback(
    (error) => {
      setError(() => {
        throw error;
      });
    },
    [setError]
  );
};

export default AgentErrorBoundary; 